import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';

import { IconComponent } from '../icon/icon.component';

@UntilDestroy()
@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  imports: [CommonModule, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * ButtonComponent
 *
 * @Description
 * This component is used to create a customizable button with various states like idle, loading, success, and error.
 * It supports multiple styling options including color, background color, border color, width, height, and size.
 * The button can be customized further with inline styles, CSS classes, and icons (prefix and postfix).
 *
 * The component relies on `FormService` to determine its disabled state based on form validity and loading status.
 * Texts for different states are localized using `TranslateService`.
 *
 * @Input() **disabled**: Manually set the disabled state of the button.
 * @Input() **isLoading**: Flag to indicate if the button is in a loading state.
 * @Input() [**color**, **backgroundColor**, **borderColor**, **width**, **height**]: Styling properties for the button.
 * @Input() **size**: The size of the button ('small', 'medium', 'large').
 * @Input() **inlineStyle**: Custom inline styles for the button.
 * @Input() **cssClass**: CSS class for additional styling.
 * @Input() **service**: FormService instance for form state management.
 * @Input() [**idleText**, loadingText, successText, errorText]: Texts for various states of the button.
 * @Input() [**postfixIconName**, **prefixIconName**, **iconColor**, **iconSize**]: Icon properties for the button.
 *
 * @Output() clickEvent: Event emitted when the button is clicked.
 *
 * @Example
 * <app-button
 *   [service]="formServiceInstance"
 *   [idleText]="'Save'"
 *   [loadingText]="'Processing'"
 *   [successText]="'Success'"
 *   [errorText]="'Error'"
 *   (clickEvent)="onClickEvent($event)">
 * </app-button>
 *
 * Note: This component uses Angular's standalone component feature and `UntilDestroy` for automatic unsubscription.
 */
export class ButtonComponent implements OnInit, OnChanges {
  @HostBinding('style.width') hostWidth: string;
  StateType = StateType;
  /**
   * Disables the button.
   */
  @Input() disabled: boolean;
  /**
   * Shows the loading state of the button. Shows spinner and disables the button.
   * default false
   */
  @Input() isLoading = false;
  /**
   * Color of the button text.
   * default #FFFFFF (white)
   */
  @Input() color: string;
  /**
   * Background color of the button.
   * default none
   */
  @Input() backgroundColor: string;
  /**
   * Border color of the button.
   * default none (none)
   */
  @Input() borderColor: string;
  /**
   * Width of the button.
   * default auto
   */
  @Input() width: string;
  /**
   * Height of the bbutton.
   * default auto
   */
  @Input() height: string;
  /**
   * Predefined size of the button.
   * default medium
   * Example: small | medium | large
   */
  @Input() size: 'xs' | 'small' | 'medium' | 'large' | 'width-full' = 'medium';
  /**
   * Inline style object.
   * Pass style object as is.
   * Example: { 'margin-right': '10px' } or { 'marginRight': '10px', 'marginLeft': '10px' }
   */
  @Input() inlineStyle: Record<string, string>;
  /**
   * CSS class of the button.
   * default button-primary
   * Example: button-primary
   *
   */
  @Input() cssClass = 'button-primary';
  /**
   * Text to be displayed when the button is in idle state.
   * default Save
   * Example: Save
   */
  @Input() idleText: string;
  /**
   * Text to be displayed when the button is in loading state.
   * default Processing...
   * Example: Processing...
   * @see loadingText
   */
  @Input() loadingText: string;
  /**
   * Text to be displayed when the button is in success state.
   * default none
   * Example: Success
   */
  @Input() successText: string;
  /**
   * Text to be displayed when the button is in error state.
   * default Error
   * Example: Error
   */
  @Input() errorText: string;
  /**
   * Current state of the button.
   * default Idle
   */
  currentState: ButtonState;
  /**
   * Event emitter for the button click.
   * default none
   */
  @Output() clickEvent = new EventEmitter<any>();
  /**
   * Type of the button.
   * default button
   */
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  /**
   * Name of the icon to be displayed after the text.
   * default none
   * Example: add | chevron-right
   */
  @Input() postfixIconName: string;
  /**
   * Name of the icon to be displayed before the text.
   * default none
   * Example: add | chevron-right
   */
  @Input() prefixIconName: string;
  /**
   * Color of the icon.
   * default #667085 (gray)
   * Example: #FFFFFF | red | rgb(255, 255, 255) | var(--white)
   */
  @Input() iconColor: string;
  /**
   * Size of the icon.
   * default 20px
   * Example: 20px
   */
  @Input() iconSize: string;
  /**
   * Placement of the icon. It will adjust the icon position based on css `position` property. all the way to right or left.
   * Positions can be tweaked using `iconPositionTop`, `iconPositionBottom`, `iconPositionLeft`, `iconPositionRight`.
   * default null
   * Example: right-end | left-end
   */
  @Input() iconPlacement: 'right-end' | 'left-end' = null;
  /**
   * Position of the icon from the top. It will adjust the icon position based on css `position` property.
   * default null
   * Example: 10px
   */
  @Input() iconPositionTop: string = null;
  /**
   * Position of the icon from the bottom. It will adjust the icon position based on css `position` property.
   * default null
   * Example: 10px
   */
  @Input() iconPositionBottom: string = null;
  /**
   * Position of the icon from the left. It will adjust the icon position based on css `position` property.
   * default null
   * Example: 10px
   */
  @Input() iconPositionLeft: string = null;
  /**
   * Position of the icon from the right. It will adjust the icon position based on css `position` property.
   * default null
   * Example: 10px
   */
  @Input() iconPositionRight: string = null;
  /**
   * Inline style object for the icon. It will be applied to icon wrapper `<span>` element.
   * Pass style object as is.
   * Example: { 'margin-right': '10px' } or { 'marginRight': '10px', 'marginLeft': '10px' }
   */
  @Input() iconInlineStyle: Record<string, string>;
  private readonly _translate = inject(TranslateService);
  private readonly _changeDetector = inject(ChangeDetectorRef);
  private _states: ButtonState[];

  get _disabled(): boolean {
    if (this.disabled !== undefined && this.disabled !== null) {
      return this.disabled;
    } else {
      return (
        this.isLoading ||
        // this.service?.form?.invalid ||
        // this.service?.isLoading ||
        // this.service?.form?.pristine ||
        this.currentState?.type === StateType.Loading ||
        this.currentState?.type === StateType.Success ||
        this.currentState?.type === StateType.Error
      );
    }
  }

  ngOnInit(): void {
    this._updateTexts();
    this.currentState = this._states[0];

    if (this.width === '100%') {
      this.hostWidth = '100%';
    }

    if (this.size == 'width-full') {
      this.width = '100%';
      this.hostWidth = '100%';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['idleText']) {
      this.idleText = changes['idleText'].currentValue;
      this._updateTexts();
      this.currentState = this._states.find((state) => state.type === StateType.Idle);
    }
  }

  click() {
    this.clickEvent.emit(this);
  }

  showIdle() {
    this.currentState = this._states.find((state) => state.type === StateType.Idle);
    this._changeDetector.markForCheck();
  }

  showLoading() {
    this.currentState = this._states.find((state) => state.type === StateType.Loading);
    this._changeDetector.markForCheck();
  }

  showSuccess() {
    this.currentState = this._states.find((state) => state.type === StateType.Success);
    this._changeDetector.markForCheck();

    setTimeout(() => {
      this.showIdle();
    }, 3000);
  }

  showError(error?: string) {
    this.currentState = this._states.find((state) => state.type === StateType.Error);
    this._changeDetector.markForCheck();
    setTimeout(() => {
      this.showIdle();
    }, 3000);
  }

  private _updateTexts() {
    this._states = [
      { type: StateType.Idle, title: this.idleText ?? this._translate.instant(this.idleText || 'buttonComponent.Save') },
      { type: StateType.Loading, title: this.loadingText ?? this._translate.instant(this.loadingText || 'buttonComponent.Processing') },
      { type: StateType.Success, title: this.successText ?? this._translate.instant(this.successText || 'buttonComponent.Success') },
      { type: StateType.Error, title: this.errorText ?? this._translate.instant(this.errorText || 'buttonComponent.Error') },
    ];

    this._changeDetector.markForCheck();
  }
}

interface ButtonState {
  type: StateType;
  title: string;
}

enum StateType {
  Idle,
  Loading,
  Success,
  Error,
}
